import {
  Button,
  type ButtonColor,
  ButtonsGroup,
  Input,
  Link,
  Modal,
  ModalProps,
  type NavItem,
  NavListItemWithBlockDescription,
  type NavListItemWithBlockDescriptionValue,
  Select,
  SkeletonBox,
  Space,
  Text,
  useModal,
} from '@billy/design-system'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchAttachment } from '@features/attachments/hooks/useFetchAttachment'
import { EInvoiceStatus } from '@features/eInvoicing/constants/eInvoiceStatus'
import { useGetEInvoicingLifecycleReasons } from '@features/eInvoicing/hooks/useGetEInvoiceLifecycleReasons'
import type { EInvoiceLifecycleAction } from '@features/eInvoicing/types'
import { EInvoiceLifecycleTrackingEvents, type EInvoiceLifecycleTrackingSource } from '@views/receipts/enums/tracking'

import { useSegment, useWatch } from '../../../../hooks'
import { useLocale } from '../../../../hooks/useLocale'
import { useLifecycleActionForm } from '../../hooks/useLifecycleActionForm'
import * as Styled from './styles'

export interface LifecycleActionModalI18nKeys {
  title: string
  message: string
  specifyReason: string
  specifyReasonError: string
  selectReason: string
  optionalComment: string
  mandatoryComment: string
  optionalCommentPlaceholder: string
  commentRequiredError: string
  bottomMessage: string
  learnMoreUrl: string
  learnMore: string
  button: string
}

interface LifecycleActionModalProps extends ModalProps {
  action: EInvoiceLifecycleAction
  attachmentId: string
  onProcessingChange?: (isProcessing: boolean) => void
  onSuccess?: () => void
  i18nKeys: LifecycleActionModalI18nKeys
  buttonColor?: ButtonColor
  trackingSource: EInvoiceLifecycleTrackingSource
  previousStatus?: EInvoiceStatus | null
}

const SKELETON_REASON_ITEMS: NavItem<NavListItemWithBlockDescriptionValue>[] = [
  { id: 'skeleton-0', value: { displayData: { title: '' } }, children: '' },
  { id: 'skeleton-1', value: { displayData: { title: '' } }, children: '' },
  { id: 'skeleton-2', value: { displayData: { title: '' } }, children: '' },
]

export const LifecycleActionModal = ({
  action,
  attachmentId,
  onProcessingChange,
  onSuccess,
  i18nKeys,
  buttonColor = 'primary',
  trackingSource,
  previousStatus,
  ...modalProps
}: LifecycleActionModalProps) => {
  const { t } = useTranslation()
  const { code } = useLocale()
  const lang = code?.startsWith('fr') ? 'fr' : 'en'
  const { close } = useModal(modalProps.id)
  const { track } = useSegment()

  // No status means e-invoice is active
  const previousStatusForTracking = previousStatus ?? EInvoiceStatus.Active

  const { attachment } = useFetchAttachment(attachmentId)

  const { data, isProcessing: isLoadingReasons } = useGetEInvoicingLifecycleReasons({
    lifecycleAction: action,
    eInvoiceId: attachment?.eInvoiceId,
  })

  const isLoadingWithoutData = !data && isLoadingReasons

  const reasonItems = data?.reasons?.map(({ code, title, description }) => ({
    id: code,
    children: title[lang],
    value: {
      displayData: {
        title: title[lang],
        description: description[lang],
      },
    },
  }))

  const mandatoryReasonCodes = useMemo(
    () => new Set(data?.reasons?.filter((r) => r.isCommentMandatory).map((r) => r.code) ?? []),
    [data?.reasons],
  )

  const { Form, FormItem, submitForm, control, clearErrors, isProcessing, isOrganizationLoading } =
    useLifecycleActionForm({
      action,
      attachmentId,
      reasonCodeRequiredMessage: t(i18nKeys.specifyReasonError),
      mandatoryReasonCodes,
      commentRequiredMessage: t(i18nKeys.commentRequiredError),
      onSuccess: (payload) => {
        track(EInvoiceLifecycleTrackingEvents.ActionSubmitted, {
          action,
          reasonCode: payload.reasonCode,
          source: trackingSource,
          previousStatus: previousStatusForTracking,
        })
        close()
        onSuccess?.()
      },
    })

  const reasonCode = useWatch({ control, name: 'reasonCode' })
  const isCommentMandatory = !!reasonCode && mandatoryReasonCodes.has(reasonCode)

  useEffect(() => {
    onProcessingChange?.(isProcessing)

    return () => {
      onProcessingChange?.(false)
    }
  }, [isProcessing, onProcessingChange])

  return (
    <Modal closable={!isProcessing} size="s" {...modalProps}>
      <Modal.Header title={t(i18nKeys.title)} />
      <Modal.Body>
        <Form>
          <Text colorVariant="secondary">{t(i18nKeys.message)}</Text>
          <Space size="s16" />
          <FormItem
            name="reasonCode"
            render={({ field }) => (
              <>
                <Text colorVariant="primary" weight="bold">
                  {t(i18nKeys.specifyReason)}
                </Text>
                <Select
                  items={isLoadingWithoutData ? SKELETON_REASON_ITEMS : (reasonItems ?? [])}
                  selectedId={field.value}
                  onSelect={(id) => {
                    if (!isLoadingWithoutData) {
                      field.onChange(id)
                      clearErrors('reason')
                    }
                  }}
                  placeholder={t(i18nKeys.selectReason)}
                  dropdownSize="fitTrigger"
                  dropdownItemRender={
                    isLoadingWithoutData
                      ? ({ item }) => (
                          <Styled.SkeletonListItem key={item.id}>
                            <SkeletonBox fullWidth height={16} />
                            <Styled.SkeletonDescriptionWrapper>
                              <SkeletonBox fullWidth height={12} />
                            </Styled.SkeletonDescriptionWrapper>
                          </Styled.SkeletonListItem>
                        )
                      : (props) => <NavListItemWithBlockDescription {...props} />
                  }
                />
              </>
            )}
          />
          <FormItem
            name="reason"
            render={({ field }) => (
              <>
                <Space size="s16" />
                <Text colorVariant="primary" weight="bold">
                  {t(isCommentMandatory ? i18nKeys.mandatoryComment : i18nKeys.optionalComment)}
                </Text>
                <Input {...field} placeholder={t(i18nKeys.optionalCommentPlaceholder)} />
              </>
            )}
          />
          <Space size="s16" />
          <Text colorVariant="primary">
            {t(i18nKeys.bottomMessage)}{' '}
            <strong>
              <Link href={t(i18nKeys.learnMoreUrl)} target="_blank" rel="noreferrer">
                {t(i18nKeys.learnMore)}
              </Link>
            </strong>
          </Text>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button
            variant="text"
            onClick={() => {
              track(EInvoiceLifecycleTrackingEvents.ActionCancelled, {
                action,
                source: trackingSource,
                previousStatus: previousStatusForTracking,
              })
              close()
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={submitForm}
            disabled={isProcessing || isOrganizationLoading || !reasonCode}
            loading={isProcessing}
            color={buttonColor}
          >
            {t(i18nKeys.button)}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
